import React, { FC } from 'react'
import styled from 'styled-components'
import { DescriptionTextStyled, PaddedContentStyled, SecondaryHeaderStyled } from '../../styles/shared'
import TextWithLinkStyled from '../common/textWithLinkStyled'
import { ScreenBreakpointSize } from '../../utils/enums'
import { getScreenBreakpointSize } from '../../utils/getScreenBreakpointSize'
import { getYearsSinceSNWasFounded } from '../../utils/getYearsSinceSNWasFounded'
import Link from '../link/Link'
import { FontWeight900 } from '../../styles/variables'
import { StaticImage } from 'gatsby-plugin-image'

const ContainerStyled = styled.div`
  display: flex;
  max-width: var(--page-title-max-width);
  margin: var(--spacing-72) auto 0;
  position: relative;
  flex-direction: column;
  align-items: flex-start;

  @media screen and (max-width: ${getScreenBreakpointSize(ScreenBreakpointSize.Large, -1)}) {
    display: block;
    margin-left: var(--spacing-16);
    margin-right: var(--spacing-16);
    background: var(--background-gray);
    border: 1px solid var(--border-gray);
    border-radius: var(--border-radius-8);
    overflow: hidden;
  }
`
const LeftTextSectionStyled = styled.div`
  width: 48%;

  @media screen and (max-width: ${getScreenBreakpointSize(ScreenBreakpointSize.Large, -1)}) {
    padding: 30px;
    width: 100%;
  }
`
const CustomDescriptionTextStyled = styled(DescriptionTextStyled)`
  &[data-name='item-1'] {
    margin-top: var(--spacing-16);
    margin-bottom: var(--spacing-16);

    @media screen and (max-width: ${getScreenBreakpointSize(ScreenBreakpointSize.Large, -1)}) {
      margin-bottom: var(--spacing-12);
    }

`
const LinkStyled = styled(Link)`
  color: var(--heading-gray);
  border-bottom: 1px dotted var(--heading-gray);

  &:hover {
    text-decoration: none;
  }
`
const FactsWithImageContainerStyled = styled.div`
  @media screen and (max-width: ${getScreenBreakpointSize(ScreenBreakpointSize.Medium, -1)}) {
    display: flex;
    flex-direction: column;
  }
`
const FactsContainerStyled = styled.div`
  display: flex;
  margin-bottom: var(--spacing-24);

  @media screen and (max-width: ${getScreenBreakpointSize(ScreenBreakpointSize.Large, -1)}) {
    margin-bottom: 0;
  }

  @media screen and (max-width: ${getScreenBreakpointSize(ScreenBreakpointSize.Medium, -1)}) {
    display: block;
    order: 1;
  }
`
const FactWrapperStyled = styled.div`
  margin-right: var(--spacing-24);
  width: 32%;

  @media screen and (max-width: ${getScreenBreakpointSize(ScreenBreakpointSize.Medium, -1)}) {
    display: flex;
    flex-direction: row-reverse;
    justify-content: space-between;
    align-items: center;
    width: auto;
    margin-right: auto;
    position: relative;
    padding-top: var(--spacing-8);
    padding-bottom: var(--spacing-8);

    &:not(:first-child)::after {
      content: '';
      width: 100%;
      height: 1px;
      background-color: var(--border-gray);
      position: absolute;
      top: 0;
    }
  }
`
const FactTitleStyled = styled.div`
  color: var(--color-blue);
  font-family: var(--lato);
  font-weight: ${FontWeight900};
  font-size: var(--font-size-36);
  line-height: var(--line-height-1_3);
`
const FactDescriptionStyled = styled.div`
  color: var(--body-gray);
  font-family: var(--avenir-next);
  font-weight: 700;
  font-size: var(--font-size-16);
  line-height: var(--line-height-1_5);
  margin-top: var(--spacing-4);
`
const ImageContainer = styled.div`
  position: absolute;
  left: 55%;
  top: 10%;

  @media screen and (max-width: ${getScreenBreakpointSize(ScreenBreakpointSize.Large, -1)}) {
    position: relative;
    left: 0;
    top: 0;
    width: 100%;
    margin-top: 0;
    margin-bottom: var(--spacing-12);
  }
`

const ImageContent = styled.div`
  box-shadow: 16px 18px 58px -16px rgba(0, 0, 0, 0.86);
  border-radius: 10px;
  overflow: hidden;

  @media screen and (max-width: ${getScreenBreakpointSize(ScreenBreakpointSize.Large, -1)}) {
    box-shadow: none;
    border-radius: 0;
  }
`

const LinkContainerStyled = styled.div`
  margin-top: var(--spacing-16);
  @media screen and (max-width: ${getScreenBreakpointSize(ScreenBreakpointSize.Large, -1)}) {
    padding: 0 30px;
    margin-bottom: var(--spacing-24);
  }
`

const yearsInBusiness = getYearsSinceSNWasFounded()
const facts = [
  {
    title: '100%',
    description: 'Revenue from paying users',
  },
  {
    title: '$0',
    description: 'In venture capital',
  },
  {
    title: `${yearsInBusiness} years`,
    description: 'In service',
  },
]

const WhoIsStandardNotes: FC = () => {
  return (
    <PaddedContentStyled>
      <ContainerStyled>
        <ImageContainer>
          <ImageContent>
            <StaticImage alt="Man With Poster" aria-hidden="true" src={'../../assets/images/cyborg-factory.jpg'} />
          </ImageContent>
        </ImageContainer>
        <LeftTextSectionStyled>
          <SecondaryHeaderStyled>Who is Standard Notes?</SecondaryHeaderStyled>
          <CustomDescriptionTextStyled data-name={'item-1'}>
            We’re an independent company founded on an ethos of software sustainability and ethical data practices. Our
            code is completely{' '}
            <LinkStyled to={'/help/46/is-standard-notes-an-open-source-notes-app'}>{'open'}</LinkStyled> and
            independently{' '}
            <LinkStyled to={'/help/2/has-standard-notes-completed-a-third-party-security-audit'}>
              {'audited'}
            </LinkStyled>{' '}
            by leading security researchers.
          </CustomDescriptionTextStyled>
          <FactsWithImageContainerStyled>
            <FactsContainerStyled>
              {facts.map(({ title, description }) => (
                <FactWrapperStyled key={title}>
                  <FactTitleStyled>{title}</FactTitleStyled>
                  <FactDescriptionStyled>{description}</FactDescriptionStyled>
                </FactWrapperStyled>
              ))}
            </FactsContainerStyled>
          </FactsWithImageContainerStyled>
        </LeftTextSectionStyled>
        <LinkContainerStyled>
          <TextWithLinkStyled text={'Read our longevity statement'} targetUrl={'/longevity'} />
        </LinkContainerStyled>
      </ContainerStyled>
    </PaddedContentStyled>
  )
}

export default WhoIsStandardNotes
